/**
 * Test health: is this test collecting data it can be read on?
 *
 * One assessment for the "Test health" card: an overall status (Healthy /
 * Check / Needs traffic), per-version Unique Visitors and Total Views, split
 * balance, engagement A vs B, daily traffic, time to a result and a run
 * timeline (started → minimum run → earliest call → auto-stop, with today).
 * Thresholds are the shared ones: LOW_TRAFFIC_DAILY_VISITORS (planner + results),
 * CANNOT_FINISH_DAYS and the zero-conversion "check setup" rule from
 * resultsStage, SPLIT_CHECK_MIN_VISITORS for the split check.
 */

import { LOW_TRAFFIC_DAILY_VISITORS, detectSampleRatioMismatch, isLowDailyTraffic } from "@/lib/utils/abTestStatistics"
import { CANNOT_FINISH_DAYS, CHECK_SETUP_MIN_VISITORS, earliestCallDay } from "@/lib/experiments/resultsStage"

/** Below this many visitors the traffic split check is noise. */
export const SPLIT_CHECK_MIN_VISITORS = 100

const DAY_MS = 24 * 60 * 60 * 1000

export type HealthStatus = "healthy" | "check" | "needs_traffic"
export type HealthItemState = "ok" | "warn" | "pending" | "na"

export const HEALTH_STATUS_LABELS: Record<HealthStatus, string> = {
  healthy: "Healthy",
  check: "Check",
  needs_traffic: "Needs traffic",
}

export interface HealthVersionInput {
  id: string
  label: string
  /** Unique Visitors. */
  visitors: number
  /** Total Views; null when the API sends none. */
  views: number | null
  /** Launch-goal conversions, for the "goal not tracked" check. */
  conversions: number
  /** Percent 0-100; null when not collected for this test type. */
  bounceRate: number | null
  /** Percent 0-100; null when not collected. */
  scrollDepth: number | null
  /** Seconds; null when not collected. */
  timeOnPage: number | null
}

export interface HealthInput {
  isActive: boolean
  startedAt: Date
  endedAt?: Date | null
  now?: Date
  /** Minimum run for the test type, in days. */
  minDays: number
  /** Recommended maximum run, in days. */
  maxDays: number
  autoStopDays?: number | null
  versions: HealthVersionInput[]
  /** Configured split as shares (0-1); even split when absent. */
  expectedShares?: number[]
  /** Days to the required sample at today's traffic, null when unknown. */
  estimatedTotalDays: number | null
  /** Earliest call day for the goal on screen (call gates), null when unknown. */
  earliestCallDay?: number | null
}

export interface HealthItem {
  label: string
  value: string
  detail?: string
  state: HealthItemState
}

export interface EngagementRow {
  key: "bounce" | "scroll" | "time"
  label: string
  /** One formatted value per version, in version order; null when not collected. */
  values: Array<string | null>
  collected: boolean
}

export interface TimelineStep {
  key: "started" | "minimum" | "call" | "stop"
  label: string
  /** Day of the test; null when it can't be dated (no auto-stop, call over a year away). */
  day: number | null
  date: Date | null
  /** done = reached, pending = still ahead, none = not set. */
  state: "done" | "pending" | "none"
  /** Short value under the label: "Oct 7", "No auto-stop", "Over a year". */
  value: string
}

export interface RunTimeline {
  steps: TimelineStep[]
  /** Day of the test today (or the last day for a finished test). */
  today: number
  /** Last dated day on the timeline. */
  span: number
  isActive: boolean
}

export interface TestHealthAssessment {
  status: HealthStatus
  label: string
  /** One sentence: why this status. */
  summary: string
  daysRunning: number
  versions: Array<{ id: string; label: string; visitors: number; views: number | null; share: number | null }>
  split: HealthItem
  traffic: HealthItem
  timeToResult: HealthItem
  timeline: RunTimeline
  engagement: EngagementRow[]
  /** Everything that pushed the status off Healthy, most important first. */
  reasons: string[]
  /** Visitors a day across versions (estimate input only, never shown as a sum). */
  dailyVisitors: number
}

const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US")
const fmtShortDate = (d: Date, now: Date) =>
  d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  })
const fmtPct = (v: number) => `${v.toFixed(1)}%`
const fmtDuration = (seconds: number) => {
  const s = Math.round(seconds)
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`
}

export function daysBetween(start: Date, end: Date): number {
  return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / DAY_MS))
}

export function assessHealth(input: HealthInput): TestHealthAssessment {
  const now = input.now ?? new Date()
  const end = input.endedAt ?? now
  const day = daysBetween(input.startedAt, input.isActive ? now : end)
  const versions = input.versions
  // Versions are disjoint by assignment, so their sum is this page's traffic for
  // the estimates below; it is never displayed as a Unique Visitors total.
  const total = versions.reduce((s, v) => s + v.visitors, 0)
  const dailyVisitors = Math.round(total / Math.max(1, day))
  const n = Math.max(1, versions.length)
  const perVersionDaily = Math.round(dailyVisitors / n)

  // ── Split balance ──
  const expected =
    input.expectedShares && input.expectedShares.length === versions.length ? input.expectedShares : undefined
  const expectedShares = expected ?? versions.map(() => 1 / n)
  const srm = versions.length >= 2 ? detectSampleRatioMismatch(versions.map((v) => v.visitors), expected) : null
  const setTo = expectedShares.map((s) => Math.round(s * 100)).join(" / ")
  const splitChecked = total >= SPLIT_CHECK_MIN_VISITORS
  const splitMismatch = Boolean(srm?.hasMismatch) && splitChecked
  const split: HealthItem =
    versions.length < 2
      ? { label: "Split balance", value: "One version", state: "na" }
      : total === 0
        ? { label: "Split balance", value: "No visitors yet", detail: `Set to ${setTo}`, state: "pending" }
        : !splitChecked
          ? {
              label: "Split balance",
              value: "Too few visitors to check",
              detail: `Checked from ${SPLIT_CHECK_MIN_VISITORS} visitors · set to ${setTo}`,
              state: "pending",
            }
          : {
              label: "Split balance",
              value: versions.map((v) => ((v.visitors / total) * 100).toFixed(1)).join(" / "),
              detail: splitMismatch ? `Set to ${setTo}: visitors are not splitting as set` : `Set to ${setTo}`,
              state: splitMismatch ? "warn" : "ok",
            }

  // ── Setup checks (resultsStage "check setup" rule) ──
  const reasons: string[] = []
  // A version with no visitors at all is the more specific (and likelier) cause
  // of a split mismatch: say that instead.
  const missing = total > 0 ? versions.find((v) => v.visitors === 0) : undefined
  if (input.isActive && missing && day > 2) {
    reasons.push(`${missing.label} has no visitors after ${day} days. Check the version is live and its URL does not redirect.`)
  } else if (splitMismatch && !missing) {
    // (One version still unseen in the first two days is normal: no check yet.)
    reasons.push(`Visitors are not splitting as set (${setTo}). Check that nothing redirects or caches one version.`)
  }
  const noConversions =
    versions.length >= 2 &&
    versions.every((v) => v.conversions === 0) &&
    versions.every((v) => v.visitors >= CHECK_SETUP_MIN_VISITORS) &&
    day >= 3
  if (noConversions) reasons.push(`No conversions in any version after ${day} days. Check the goal is tracked on this page.`)
  const check = reasons.length > 0

  // ── Traffic and time to a result ──
  const low = input.isActive && total > 0 && isLowDailyTraffic(dailyVisitors)
  const tooLong =
    input.isActive && input.estimatedTotalDays != null && input.estimatedTotalDays > CANNOT_FINISH_DAYS && total > 0
  const traffic: HealthItem = !input.isActive
    ? { label: "Daily traffic", value: total > 0 ? `~${fmtInt(perVersionDaily)} a day per version` : "No visitors", state: "na" }
    : total === 0
      ? { label: "Daily traffic", value: "No visitors yet", state: "pending" }
      : {
          label: "Daily traffic",
          value: `~${fmtInt(perVersionDaily)} a day per version`,
          detail: low ? "Low: a result will take a long time" : undefined,
          state: low ? "warn" : "ok",
        }

  const timeToResult: HealthItem = (() => {
    const label = "Time to result"
    if (!input.isActive) return { label, value: `Ran ${day} ${day === 1 ? "day" : "days"}`, state: "na" as const }
    if (total === 0 || day < 2 || input.estimatedTotalDays == null) {
      return { label, value: "Too soon to estimate", detail: "Estimate after 2 to 3 days of traffic", state: "pending" as const }
    }
    if (input.estimatedTotalDays > CANNOT_FINISH_DAYS) {
      return {
        label,
        value: input.estimatedTotalDays > 365 ? "More than a year" : `About ${Math.round(input.estimatedTotalDays / 30)} months`,
        detail: "Too long at this traffic: send more visitors or test a busier page",
        state: "warn" as const,
      }
    }
    const callDay = earliestCallDay(input.minDays, input.estimatedTotalDays)
    const date = new Date(input.startedAt.getTime() + callDay * DAY_MS)
    return {
      label,
      value: `Around ${fmtShortDate(date, now)}`,
      detail: callDay > day ? `Day ${day} of about ${callDay}` : "Enough data for the minimum run",
      state: "ok" as const,
    }
  })()

  if (!check && input.isActive) {
    if (total === 0) reasons.push("No visitors yet. If the page gets traffic, check the Humblytics script is installed there.")
    else if (low) reasons.push(`About ${fmtInt(dailyVisitors)} visitors a day reach this test, under ${LOW_TRAFFIC_DAILY_VISITORS} a day.`)
    else if (tooLong) reasons.push("At this traffic a result would take more than six months.")
  }

  const status: HealthStatus = check ? "check" : reasons.length > 0 ? "needs_traffic" : "healthy"

  // ── Run timeline ──
  const at = (d: number) => new Date(input.startedAt.getTime() + d * DAY_MS)
  const callDay = input.earliestCallDay ?? null
  const stopDay = input.autoStopDays ?? null
  const steps: TimelineStep[] = [
    { key: "started", label: "Started", day: 0, date: input.startedAt, state: "done", value: fmtShortDate(input.startedAt, now) },
    {
      key: "minimum",
      label: "Minimum run",
      day: input.minDays,
      date: at(input.minDays),
      state: day >= input.minDays ? "done" : "pending",
      value: fmtShortDate(at(input.minDays), now),
    },
    callDay != null && callDay <= 365
      ? { key: "call", label: "Earliest call", day: callDay, date: at(callDay), state: day >= callDay ? "done" : "pending", value: fmtShortDate(at(callDay), now) }
      : { key: "call", label: "Earliest call", day: null, date: null, state: "none", value: callDay == null ? "Not known yet" : "Over a year" },
    stopDay
      ? { key: "stop", label: "Auto-stop", day: stopDay, date: at(stopDay), state: day >= stopDay ? "done" : "pending", value: fmtShortDate(at(stopDay), now) }
      : { key: "stop", label: "Auto-stop", day: null, date: null, state: "none", value: "No auto-stop" },
  ]
  const dated = steps.map((s) => s.day ?? 0)
  const timeline: RunTimeline = {
    steps,
    today: day,
    span: Math.max(day, ...dated, 1),
    isActive: input.isActive,
  }

  // ── Engagement A vs B ──
  const row = (
    key: EngagementRow["key"],
    label: string,
    pick: (v: HealthVersionInput) => number | null,
    fmt: (x: number) => string,
  ): EngagementRow => {
    const vals = versions.map((v) => (v.visitors > 0 ? pick(v) : null))
    const collected = versions.some((v) => pick(v) != null)
    return { key, label, values: vals.map((x) => (x == null ? null : fmt(x))), collected }
  }
  const engagement = [
    row("bounce", "Bounce rate", (v) => v.bounceRate, fmtPct),
    row("scroll", "Scroll depth", (v) => v.scrollDepth, (x) => `${Math.round(x)}%`),
    row("time", "Time on page", (v) => v.timeOnPage, fmtDuration),
  ]

  const summary =
    status === "healthy"
      ? input.isActive
        ? total > 0
          ? "Collecting data as planned."
          : "Waiting for the first visitors."
        : `Ran ${day} ${day === 1 ? "day" : "days"} with a clean split.`
      : reasons[0]

  return {
    status,
    label: HEALTH_STATUS_LABELS[status],
    summary,
    daysRunning: day,
    versions: versions.map((v) => ({
      id: v.id,
      label: v.label,
      visitors: v.visitors,
      views: v.views,
      share: total > 0 ? v.visitors / total : null,
    })),
    split,
    traffic,
    timeToResult,
    timeline,
    engagement,
    reasons,
    dailyVisitors,
  }
}
